/**
 * tm/components/RecordingView.tsx — 동행상담 녹음 · 화자별 대화 글 (2026-10-28 지도사 「클로바노트처럼 · 발언 클릭하면 재생」)
 *  로움 어드민(고치기 가능) · FP 포털 · WM 포털(보기만)이 같이 쓴다 — 글자 크기는 size 로(어드민 13 · 포털 15)
 *  발언을 누르면 그 시점부터 재생 · 재생 중인 발언은 바탕색 · 같은 화자가 이어 말하면 한 묶음 · 낱말 찾기
 *  서버 routes/consult-rec · services/consultRec(segments [{ s, e, spk, text }] 밀리초)
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { EditIcon } from './ui';

export interface RecSeg { s: number; e: number; spk: string; text: string }
export interface Rec {
  id: string; file_name: string; size: number; duration_ms: number | null; status: 'queued' | 'processing' | 'done' | 'failed' | string; error: string;
  created_at: string; created_by?: string; audio_url: string; segments: RecSeg[]; speakers: Record<string, string>;
  /** 녹음 파일 보관(2026-10-28 · 30일) — 지운 때 · 지울 날 */
  audio_deleted_at?: string | null; audio_until?: string | null;
  /** 화자 수(2026-10-29) — 고른 수(null 자동) · 다시 나누는 중이면 그 수 · 안 됐으면 까닭 */
  speaker_count?: number | null; relabeling?: number | null; relabel_error?: string;
}

const INK = '#191F28', SUB = '#6B7684', LINE = '#E5E8EB', SOFT = '#F2F4F6';
const SPK = ['#2E75B6', '#059669', '#B45309', '#7C3AED', '#BE185D', '#0E7490'];   // 화자 이름 글자색만(왼쪽 색 막대 X)

/** 화자 수 고르기 칸(2026-10-29 지도사 「화자가 몇 명인지 내가 고르면 그 범위 안에서」) — 올릴 때(자동 포함) · 고칠 때 같은 모양 · 같은 폭 */
export const SPEAKER_NS = [2, 3, 4, 5, 6, 7, 8];
export function SpeakerCountChips({ value, onPick, auto = false, size = 'admin', accent = '#2E75B6', disabled = false }: {
  value: number; onPick: (n: number) => void; auto?: boolean; size?: 'admin' | 'portal'; accent?: string; disabled?: boolean;
}) {
  const h = size === 'portal' ? 40 : 30, w = size === 'portal' ? 44 : 36, fsS = size === 'portal' ? 13 : 12;
  const opts = (auto ? [0] : []).concat(SPEAKER_NS);
  return (
    <span role="radiogroup" aria-label="화자 수" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {opts.map(n => {
        const on = value === n;
        return (
          <button key={n} type="button" role="radio" aria-checked={on} disabled={disabled} onClick={() => onPick(n)}
            style={{ width: n === 0 ? w + 12 : w, height: h, borderRadius: 8, border: `1px solid ${on ? accent : LINE}`, background: on ? accent : '#fff', color: on ? '#fff' : INK, fontSize: fsS, fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums', opacity: disabled ? 0.5 : 1 }}>
            {n === 0 ? '자동' : `${n}명`}
          </button>
        );
      })}
    </span>
  );
}

/** 「11/27」 */
function mdOf(iso: string): string { const ms = new Date(iso).getTime(); if (isNaN(ms)) return ''; const k = new Date(ms + 9 * 3600 * 1000); return `${k.getUTCMonth() + 1}/${k.getUTCDate()}`; }

export function clock(ms: number | null | undefined): string {
  const t = Math.max(0, Math.floor((ms || 0) / 1000)), h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

/** 찾은 낱말 표시 */
function marked(text: string, q: string): ReactNode {
  if (!q) return text;
  const parts = text.split(q);
  return parts.map((p, i) => <Fragment key={i}>{p}{i < parts.length - 1 && <mark style={{ background: '#FFE58F', color: INK, borderRadius: 3, padding: '0 1px' }}>{q}</mark>}</Fragment>);
}

export default function RecordingView({ rec, size = 'admin', accent = '#2E75B6', editable = false, keyReady = true, defaultOpen = false, title, memo, onMemo, onRename, onMerge, onRetry, onDelete, onSpeakerCount }: {
  rec: Rec; size?: 'admin' | 'portal'; accent?: string; editable?: boolean; keyReady?: boolean;
  /** 처음부터 펼쳐 둘지 — 목록에서는 닫아 두고 누르면 듣기 · 대화 글(2026-10-28 지도사 「평소에 닫아놨다가 누르면 밑에 열리게」) · 녹음 하나만 보는 화면은 true */
  defaultOpen?: boolean;
  /** 머리 제목(없으면 파일 이름) — 통화 녹음은 「10/28(화) 오전 10:22 통화」 */
  title?: string;
  /** 통화 주제(올릴 때 적은 한 줄) — 제목 바로 아래 · onMemo 가 있으면 고칠 수 있음(2026-10-28) */
  memo?: string; onMemo?: (memo: string) => void;
  onRename?: (speakers: Record<string, string>) => void; onRetry?: () => void; onDelete?: () => void;
  /** 화자 합치기 — 같은 사람이 둘로 나뉘었을 때(2026-10-28) */
  onMerge?: (from: string, to: string) => void;
  /** 화자 수를 정해 화자만 다시 나누기 — 받아쓴 글은 그대로(2026-10-29) · 없으면 예전처럼 「화자 다시 나누기」(처음부터 정리) */
  onSpeakerCount?: (n: number) => void;
}) {
  const fs = size === 'portal' ? 15 : 13, fsS = size === 'portal' ? 13 : 12;
  const audio = useRef<HTMLAudioElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [cur, setCur] = useState(-1);
  const [q, setQ] = useState('');
  const [names, setNames] = useState<Record<string, string> | null>(null);   // 화자 이름 고치는 중
  const [armDel, setArmDel] = useState(false);
  const [memoEdit, setMemoEdit] = useState<string | null>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [pickN, setPickN] = useState(false);   // 화자 수 고르는 중
  const segs = useMemo(() => rec.segments || [], [rec.segments]);
  const order = useMemo(() => Array.from(new Set(segs.map(x => x.spk))), [segs]);
  const colorOf = (spk: string) => SPK[Math.max(0, order.indexOf(spk)) % SPK.length];
  const hits = useMemo(() => (q.trim() ? segs.map((x, i) => (x.text.includes(q.trim()) ? i : -1)).filter(i => i >= 0) : []), [segs, q]);

  // 같은 화자가 이어 말하면 한 묶음
  const groups = useMemo(() => {
    const g: { spk: string; items: { seg: RecSeg; i: number }[] }[] = [];
    segs.forEach((seg, i) => { const last = g[g.length - 1]; if (last && last.spk === seg.spk) last.items.push({ seg, i }); else g.push({ spk: seg.spk, items: [{ seg, i }] }); });
    return g;
  }, [segs]);

  useEffect(() => {
    const a = audio.current; if (!a) return undefined;
    const on = () => { const t = a.currentTime * 1000; const i = segs.findIndex(x => t >= x.s && t < x.e + 300); setCur(i); };
    a.addEventListener('timeupdate', on);
    return () => a.removeEventListener('timeupdate', on);
  }, [segs]);

  const seek = (i: number) => {
    const a = audio.current; if (!a || !segs[i]) return;
    a.currentTime = segs[i].s / 1000;
    void a.play().catch(() => undefined);
    setCur(i);
  };
  const goHit = (dir: 1 | -1) => {
    if (!hits.length) return;
    const after = dir === 1 ? hits.find(i => i > cur) : [...hits].reverse().find(i => i < cur);
    const i = after == null ? (dir === 1 ? hits[0] : hits[hits.length - 1]) : after;
    setCur(i);
    const el = listRef.current && listRef.current.querySelector(`[data-seg="${i}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const btn = (strong = false): CSSProperties => ({ height: size === 'portal' ? 40 : 30, padding: '0 12px', borderRadius: 8, border: strong ? 'none' : `1px solid ${LINE}`, background: strong ? accent : '#fff', color: strong ? '#fff' : INK, fontSize: fsS, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' });
  const meta = [clock(rec.duration_ms) !== '0:00' ? clock(rec.duration_ms) : '', rec.audio_deleted_at ? '' : `${(rec.size / 1048576).toFixed(rec.size > 10485760 ? 0 : 1)}MB`, title ? '' : String(rec.created_at || '').slice(0, 10)].filter(Boolean).join(' · ');   // 제목에 날짜가 있으면 또 쓰지 않음
  // 닫혀 있을 때 머리에 붙는 상태(정리 중 · 못 함)
  const state = rec.status === 'done' ? (rec.relabeling ? '화자 나누는 중' : '') : rec.status === 'failed' ? '정리 못 함' : rec.status === 'queued' && !keyReady ? '정리 대기' : '정리 중';
  const lines = segs.length;

  return (
    <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, background: '#fff', overflow: 'hidden', fontFamily: 'inherit' }}>
      {/* 머리 — 누르면 듣기 · 대화 글이 열리고 닫힘 */}
      <div style={{ padding: '12px 14px', borderBottom: open ? `1px solid ${LINE}` : 'none', display: 'grid', gap: 8 }}>
        <button type="button" aria-expanded={open} onClick={() => setOpen(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, width: '100%', padding: 0, border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
          <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: fs, fontWeight: 700, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={rec.file_name}>{title || rec.file_name}</span>
          {state && <span style={{ fontSize: fsS, fontWeight: 700, color: rec.status === 'failed' ? '#DC2626' : SUB, whiteSpace: 'nowrap' }}>{state}</span>}
          <span style={{ fontSize: fsS, color: SUB, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{meta}</span>
          <span style={{ fontSize: fsS, fontWeight: 600, color: accent, whiteSpace: 'nowrap' }}>{open ? '접기' : rec.status === 'done' && lines ? '대화 글 보기' : '열기'}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><path d="m6 9 6 6 6-6" /></svg>
        </button>
        {/* 통화 주제 — 제목 바로 아래 한 줄 · 없으면 「통화 주제 적기」 */}
        {memoEdit !== null ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={memoEdit} autoFocus maxLength={200} onChange={e => setMemoEdit(e.target.value)} placeholder="예) 중진공 실사 결과 · 부지 매입 계획"
              onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { if (onMemo) onMemo(memoEdit.trim()); setMemoEdit(null); } if (e.key === 'Escape') setMemoEdit(null); }}
              style={{ flex: 1, minWidth: 0, height: 32, border: `1.5px solid ${LINE}`, borderRadius: 8, padding: '0 10px', fontSize: fs, fontFamily: 'inherit' }} />
            <button type="button" style={{ ...btn(true), height: 32 }} onClick={() => { if (onMemo) onMemo(memoEdit.trim()); setMemoEdit(null); }}>저장</button>
            <button type="button" style={{ ...btn(), height: 32 }} onClick={() => setMemoEdit(null)}>취소</button>
          </div>
        ) : (memo || (editable && onMemo)) ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {memo ? <span style={{ fontSize: fs, color: '#4E5968', lineHeight: 1.5, wordBreak: 'keep-all', minWidth: 0 }}>{memo}</span> : null}
            {editable && onMemo && (memo
              ? <EditIcon label="통화 주제 고치기" onClick={() => setMemoEdit(memo)} />
              : <button type="button" onClick={() => setMemoEdit('')} style={{ border: 'none', background: 'none', padding: 0, fontSize: fsS, color: accent, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ 통화 주제 적기</button>)}
          </div>
        ) : null}
        {open && rec.audio_url && <audio ref={audio} src={rec.audio_url} controls preload="metadata" style={{ width: '100%', height: 36 }} />}
        {/* 녹음 파일 30일 보관(2026-10-28 지도사 「1달 뒤에는 텍스트만 · 안내도 어디 적고」) — 정리가 끝난 녹음만 지운다 */}
        {!open ? null : rec.audio_deleted_at
          ? <div style={{ fontSize: fsS, color: SUB, lineHeight: 1.5 }}>녹음 파일은 30일 보관이라 지웠어요 · 대화 글은 그대로 남아요</div>
          : rec.status === 'done' && rec.audio_until ? <div style={{ fontSize: fsS, color: SUB, lineHeight: 1.5 }}>녹음 파일은 {mdOf(rec.audio_until)}까지 보관해요 · 그 뒤엔 대화 글만 남아요</div> : null}
        {/* 화자 다시 나누는 중 · 안 됐을 때 — 대화 글은 그대로 보임 */}
        {open && rec.status === 'done' && rec.relabeling ? <div role="status" style={{ fontSize: fsS, color: accent, fontWeight: 600, lineHeight: 1.5 }}>화자를 {rec.relabeling}명 안에서 다시 나누고 있어요 · 몇 분 걸려요 · 끝나면 저절로 바뀌어요</div> : null}
        {open && rec.status === 'done' && !rec.relabeling && rec.relabel_error && editable ? <div role="alert" style={{ fontSize: fsS, color: '#DC2626', lineHeight: 1.5 }}>{rec.relabel_error}</div> : null}
      </div>

      {!open ? null : rec.status !== 'done' ? (
        <div style={{ padding: '16px 14px', fontSize: fs, color: SUB, lineHeight: 1.6, wordBreak: 'keep-all' }}>
          {rec.status === 'failed'
            ? <>대화 글로 정리하지 못했어요{editable && rec.error ? <span style={{ display: 'block', fontSize: fsS }}>{rec.error}</span> : null}</>
            : rec.status === 'queued' && !keyReady
              ? (editable ? '녹음은 저장했어요. 녹음 정리 열쇠(Groq)를 서버에 넣으면 저절로 대화 글로 정리돼요' : '대화 글을 준비하고 있어요')
              : '대화 글로 정리하고 있어요. 녹음 1시간에 15분쯤 걸려요'}
          {editable && rec.status === 'failed' && onRetry && !rec.audio_deleted_at && <div style={{ marginTop: 8 }}><button type="button" style={btn()} onClick={onRetry}>다시 정리</button></div>}
        </div>
      ) : (
        <>
          {/* 화자가 너무 많이 나뉘었으면 바로 여기서 묻기(2026-10-29 지도사 「4명인데 33명」 — 맨 아래 단추는 눈에 안 띔) */}
          {editable && onSpeakerCount && !rec.relabeling && !rec.audio_deleted_at && order.length > Math.max(5, rec.speaker_count || 0) && names === null && (
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${LINE}`, background: '#FFFBEB', display: 'grid', gap: 8 }}>
              <span style={{ fontSize: fsS, fontWeight: 700, color: INK, lineHeight: 1.5 }}>화자가 {order.length}명으로 나뉘었어요 · 실제로 몇 명이 말했나요? 고르면 그 수 안에서 다시 나눠요(받아쓴 글은 그대로)</span>
              <SpeakerCountChips value={0} size={size} accent={accent} onPick={n => onSpeakerCount(n)} />
            </div>
          )}
          {/* 화자 · 찾기 */}
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${LINE}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            {names === null ? (
              <>
                {/* 이름이 같은 화자는 한 번만(같은 사람이 둘로 나뉘었어도 칩은 하나 · 2026-10-28) */}
                {order.filter((k, i) => order.findIndex(x => (rec.speakers[x] || `화자 ${x}`) === (rec.speakers[k] || `화자 ${k}`)) === i).map(k => <span key={k} style={{ fontSize: fsS, fontWeight: 700, color: colorOf(k), background: SOFT, borderRadius: 12, padding: '3px 10px' }}>{rec.speakers[k] || `화자 ${k}`}</span>)}
              </>
            ) : (
              <>
                {order.map(k => (
                  <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: fsS, color: SUB }}>
                      {`화자 ${k}`}
                      <input value={names[k] || ''} maxLength={20} onChange={e => setNames(n => ({ ...(n || {}), [k]: e.target.value }))} placeholder="예) 대표님"
                        style={{ width: 96, height: 28, border: `1.5px solid ${LINE}`, borderRadius: 6, padding: '0 8px', fontSize: fsS, fontFamily: 'inherit', color: colorOf(k), fontWeight: 700 }} />
                    </label>
                    {/* 같은 사람 — 이 화자의 말을 고른 화자로 합침(2026-10-28 지도사 「화자가 셋으로 보이면 내가 같은 사람으로 합칠 수 있어야」) */}
                    {onMerge && order.length > 1 && (
                      <select value="" aria-label={`화자 ${k} 합치기`} title="같은 사람이면 골라서 합쳐요" onChange={e => { const to = e.target.value; if (to) { setNames(null); onMerge(k, to); } }}
                        style={{ width: 104, height: 28, border: `1px solid ${LINE}`, borderRadius: 6, fontSize: fsS, color: SUB, background: '#fff', fontFamily: 'inherit', padding: '0 4px' }}>
                        <option value="">같은 사람…</option>
                        {order.filter(x => x !== k).map(x => <option key={x} value={x}>{`화자 ${x}`}{rec.speakers[x] ? ` (${rec.speakers[x]})` : ''}과 합치기</option>)}
                      </select>
                    )}
                  </span>
                ))}
                <button type="button" style={{ ...btn(true), height: 28 }} onClick={() => { if (onRename && names) onRename(names); setNames(null); }}>저장</button>
                <button type="button" style={{ ...btn(), height: 28 }} onClick={() => setNames(null)}>취소</button>
              </>
            )}
            <span style={{ flex: 1 }} />
            <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') goHit(e.shiftKey ? -1 : 1); }} placeholder="낱말 찾기" aria-label="대화 글에서 찾기"
              style={{ width: 132, height: 30, border: `1px solid ${LINE}`, borderRadius: 8, padding: '0 10px', fontSize: fsS, fontFamily: 'inherit' }} />
            {q.trim() && <span style={{ fontSize: fsS, color: SUB, whiteSpace: 'nowrap' }}>{hits.length}곳{hits.length > 0 && <> <button type="button" aria-label="앞 찾기" style={{ ...btn(), height: 26, padding: '0 6px' }} onClick={() => goHit(-1)}>‹</button> <button type="button" aria-label="다음 찾기" style={{ ...btn(), height: 26, padding: '0 6px' }} onClick={() => goHit(1)}>›</button></>}</span>}
          </div>
          {/* 대화 — 발언을 누르면 그 시점부터 */}
          <div ref={listRef} style={{ maxHeight: size === 'portal' ? '60vh' : 440, overflowY: 'auto', padding: '6px 6px 10px' }}>
            {groups.map((g, gi) => (
              <div key={gi} style={{ padding: '8px 8px 2px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: fsS, fontWeight: 700, color: colorOf(g.spk) }}>{rec.speakers[g.spk] || `화자 ${g.spk}`}</span>
                  <span style={{ fontSize: fsS - 1, color: SUB, fontVariantNumeric: 'tabular-nums' }}>{clock(g.items[0].seg.s)}</span>
                </div>
                {g.items.map(({ seg, i }) => (
                  <button key={i} type="button" data-seg={i} onClick={() => seek(i)} title={rec.audio_url ? `${clock(seg.s)}부터 듣기` : undefined}
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 8, padding: '4px 8px', margin: '1px 0', background: i === cur ? accent + '1A' : 'transparent', color: INK, fontSize: fs, lineHeight: 1.65, cursor: rec.audio_url ? 'pointer' : 'text', fontFamily: 'inherit', wordBreak: 'keep-all' }}>
                    {marked(seg.text, q.trim())}
                  </button>
                ))}
              </div>
            ))}
            {!segs.length && <div style={{ padding: 16, fontSize: fs, color: SUB }}>알아들은 말이 없어요</div>}
          </div>
        </>
      )}

      {/* 고치기 — 화자 칩과 섞이면 칩처럼 보여서 맨 아래로(2026-10-28 지도사 「UI 가 어색」) */}
      {open && editable && (onDelete || (rec.status === 'done' && (onRename || onRetry || onSpeakerCount))) && (
        <div style={{ padding: '8px 14px', borderTop: `1px solid ${LINE}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          {rec.status === 'done' && onRename && names === null && <button type="button" style={{ ...btn(), height: 28 }} onClick={() => setNames({ ...rec.speakers })}>화자 이름 · 합치기</button>}
          {/* 화자 수가 틀렸을 때 — 몇 명인지 골라 화자만 다시(2026-10-29 · 받아쓴 글 그대로) · 고르기가 없는 화면은 처음부터 다시 정리(2026-10-28) */}
          {rec.status === 'done' && !rec.audio_deleted_at && (onSpeakerCount
            ? names === null && <button type="button" disabled={!!rec.relabeling} aria-expanded={pickN} style={{ ...btn(), height: 28, borderColor: pickN ? accent : LINE, opacity: rec.relabeling ? 0.5 : 1 }} onClick={() => setPickN(v => !v)}>화자 수 {rec.speaker_count ? `${rec.speaker_count}명` : '정하기'}</button>
            : onRetry && <button type="button" style={{ ...btn(), height: 28 }} onClick={onRetry}>화자 다시 나누기</button>)}
          <span style={{ flex: 1 }} />
          {onDelete && <button type="button" style={{ ...btn(), height: 28, color: armDel ? '#fff' : '#DC2626', background: armDel ? '#DC2626' : '#fff', borderColor: armDel ? '#DC2626' : LINE }}
            onClick={() => { if (armDel) { setArmDel(false); onDelete(); } else setArmDel(true); }} onBlur={() => setArmDel(false)}>{armDel ? '한 번 더 누르면 지워요' : '녹음 지우기'}</button>}
          {pickN && onSpeakerCount && rec.status === 'done' && !rec.relabeling && (
            <div style={{ flexBasis: '100%', display: 'grid', gap: 6, marginTop: 4, padding: '10px 12px', borderRadius: 8, background: SOFT }}>
              <span style={{ fontSize: fsS, fontWeight: 700, color: INK }}>몇 명이 말했나요?</span>
              <SpeakerCountChips value={rec.speaker_count || 0} size={size} accent={accent} onPick={n => { setPickN(false); onSpeakerCount(n); }} />
              <span style={{ fontSize: fsS, color: SUB, lineHeight: 1.5, wordBreak: 'keep-all' }}>받아쓴 글은 그대로 두고 화자만 그 수 안에서 다시 나눠요 · 몇 분 걸려요 · 화자 이름은 다시 적어 주세요</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
